import { useState, useEffect, useMemo } from "react";
import NumberInput from "../../../components/NumberInput";
import { formatNumberInput } from "../../../utils/helpers/numberInput";
import { toast } from "react-hot-toast";

import { formatIDR } from "../../../utils/helpers/formatIDR";
import UserAvatar from "../../../components/UserAvatar";
import {
  calculateBaseQuantity,
  calculateIngredientCost,
  getBaseUnitLabel,
  getBaseUnitValue,
  getLatestUnitCost,
  getPriceUnitLabel,
  isWeightOrVolumeUnit,
} from "../../../utils/helpers/pricing";
import { toUnitOptions } from "../../../utils/helpers/unitOptions";
import { formatRecipeDecimal, isPositiveRecipeDecimal, sanitizeRecipeDecimalInput } from "./recipeNumberInput";
import CopyRecipeModal from "./CopyRecipeModal";
import { buildRecipeDraft, mergeRecipeDraft } from "./recipeCopy";
import { FormValidationNotice } from "../../../components/FormValidation";
import AkasSelect from "../../../components/AkasSelect";
import { AlertConfirmModal } from "../../../components/Modals/AlertConfirmModal";
import "./style.css";

const formatPercent = (value) =>
  `${Number(value || 0).toLocaleString("id-ID", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  })}%`;

const getQuantityInputWidth = (value) => {
  const digitCount = formatNumberInput(value, { editing: true }).length;
  return `${Math.min(20, Math.max(12, digitCount + 8))}ch`;
};

const formatQuantity = (value) =>
  Number(value || 0).toLocaleString("id-ID", {
    maximumFractionDigits: 4,
  });

const getPriceTrendMeta = (trend = {}) => {
  if (trend.direction === "up") {
    return {
      className: "bg-danger-lt text-danger",
      icon: "↑",
      label: `Naik ${formatPercent(Math.abs(trend.change_percent || 0))}`,
    };
  }

  if (trend.direction === "down") {
    return {
      className: "bg-success-lt text-success",
      icon: "↓",
      label: `Turun ${formatPercent(Math.abs(trend.change_percent || 0))}`,
    };
  }

  return {
    className: "bg-secondary-lt text-secondary",
    icon: "→",
    label: "Stabil",
  };
};

const FormProductRecipe = (props) => {
  const { closeModal, formState, materials, units, addProductRecipe, updateProductRecipe, fetchRecipeSources } = props;
  const optionUnit = useMemo(() => toUnitOptions(units), [units]);

  // State untuk menyimpan bahan baku yang dipilih
  const [selectedMaterials, setSelectedMaterials] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [stockFilter, setStockFilter] = useState("all");
  const [activeMobilePanel, setActiveMobilePanel] = useState("recipe");
  const [isEdit, setIsEdit] = useState(false);
  const [isCopyRecipeOpen, setIsCopyRecipeOpen] = useState(false);
  const [isCopySourcesLoading, setIsCopySourcesLoading] = useState(false);
  const [recipeSources, setRecipeSources] = useState([]);
  const [validationAttempted, setValidationAttempted] = useState(false);

  const categoryOptions = useMemo(() => {
    const categories = new Map();

    (materials || []).forEach((material) => {
      const value = material?.category;
      if (!value) return;
      categories.set(value, material?.category_label || value);
    });

    return [
      { value: "all", label: "Semua kategori" },
      ...Array.from(categories, ([value, label]) => ({ value, label }))
        .sort((first, second) => String(first.label).localeCompare(String(second.label), "id")),
    ];
  }, [materials]);

  // Filter materials berdasarkan pencarian, kategori, dan ketersediaan stok.
  const filteredMaterials = useMemo(() => {
    const keyword = searchTerm.trim().toLowerCase();

    return (materials || []).filter((material) => {
      const matchesKeyword = !keyword ||
        material.name?.toLowerCase().includes(keyword) ||
        material.code?.toLowerCase().includes(keyword);
      const matchesCategory = categoryFilter === "all" || String(material?.category) === String(categoryFilter);
      const matchesStock = stockFilter === "all" || Number(material?.stock || 0) > 0;

      return matchesKeyword && matchesCategory && matchesStock;
    });
  }, [categoryFilter, materials, searchTerm, stockFilter]);
  const hasActiveCatalogFilter = Boolean(searchTerm.trim()) || categoryFilter !== "all" || stockFilter !== "all";

  // Load data bahan baku yang sudah ada saat edit
  useEffect(() => {
    if (formState?.materials && formState?.materials.length > 0) {
      const existingMaterials = formState?.materials?.map((item) => {
        const currentMaterial = materials?.find((material) => String(material.id) === String(item.material_id)) || {};
        const unit = item.unit || currentMaterial.unit;
        const unitCost = getLatestUnitCost(currentMaterial) || item.unit_cost;
        const weight = formatRecipeDecimal(item?.weight ?? 0);
        const qty = formatRecipeDecimal(item?.qty ?? 1) || "1";
        const subTotal = calculateIngredientCost({ ...item, unit, unit_cost: unitCost, weight, qty }, optionUnit);

        return {
          ...currentMaterial,
          id: item.material_id,
          name: item.material_name,
          unit,
          unit_cost: unitCost,
          qty,
          weight,
          sub_total: subTotal,
        };
      });

      setSelectedMaterials(existingMaterials);
      setIsEdit(true);
    } else {
      setSelectedMaterials([]);
      setIsEdit(false);
    }
    setValidationAttempted(false);
    setSearchTerm("");
    setCategoryFilter("all");
    setStockFilter("all");
    setActiveMobilePanel("recipe");
  }, [formState?.materials, formState?.weight, materials, optionUnit]);

  // Handle checklist bahan baku
  const handleMaterialToggle = (material) => {
    setSelectedMaterials((currentMaterials) => {
      const isSelected = currentMaterials.some((currentMaterial) => currentMaterial.id === material.id);

      if (isSelected) {
        return currentMaterials.filter((currentMaterial) => currentMaterial.id !== material.id);
      }

      // Jumlah berat/volume dikosongkan agar user menentukan komposisi secara sadar.
      const unitCost = getLatestUnitCost(material);
      const weight = "";
      const sub_total = calculateIngredientCost({ ...material, unit_cost: unitCost, qty: 1, weight }, optionUnit);

      return [
        ...currentMaterials,
        {
          ...material,
          id: material.id,
          name: material.name,
          unit: material.unit,
          unit_cost: unitCost,
          price_trend: material?.price_trend,
          qty: "1",
          weight,
          sub_total: sub_total,
        },
      ];
    });
  };

  // Hitung total biaya bahan baku
  const calculateTotalCost = () => {
    const total = selectedMaterials?.reduce((total, material) => total + (material?.sub_total || 0), 0);
    return total;
  };
  const risingMaterials = selectedMaterials.filter((material) => material?.price_trend?.direction === "up");
  const fallingMaterials = selectedMaterials.filter((material) => material?.price_trend?.direction === "down");
  const hasInvalidQuantity = selectedMaterials.some((material) => {
    const quantity = isWeightOrVolumeUnit(material?.unit, optionUnit) ? material?.weight : material?.qty;
    return !isPositiveRecipeDecimal(quantity);
  });
  const hasUnsafeMargin =
    Number(formState?.unit_price || 0) > 0 && calculateTotalCost() >= Number(formState?.unit_price || 0);
  const productPrice = Number(formState?.unit_price || 0);
  const recipeCost = calculateTotalCost();
  const grossMargin = productPrice - recipeCost;
  const grossMarginPercent = productPrice > 0 ? (grossMargin / productPrice) * 100 : 0;
  const canAssessMargin = productPrice > 0 && selectedMaterials.length > 0 && !hasInvalidQuantity;
  const marginStatus = !canAssessMargin
    ? { label: "Belum dihitung", className: "is-neutral" }
    : hasUnsafeMargin
      ? { label: "Perlu diperiksa", className: "is-danger" }
      : grossMarginPercent < 30
        ? { label: "Margin tipis", className: "is-warning" }
        : { label: "Margin sehat", className: "is-healthy" };

  // Siapkan data resep untuk disimpan
  const prepareRecipeData = () => {
    const recipe = selectedMaterials?.map((material) => ({
      material_id: material?.id,
      material_name: material?.name,
      unit: material?.unit,
      unit_cost: Number(material?.unit_cost || 0),
      qty: Number(material?.qty || 0),
      weight: Number(material?.weight || 0),
      sub_total: Number(material?.sub_total || 0),
    }));

    return {
      ...formState,
      product_id: formState?.id,
      materials: recipe,
      total_cost: calculateTotalCost(),
    };
  };

  const [isSaving, setIsSaving] = useState(false);

  const handleOpenCopyRecipe = async () => {
    if (isCopySourcesLoading) return;

    setIsCopyRecipeOpen(true);
    setIsCopySourcesLoading(true);
    try {
      const sources = await fetchRecipeSources(500);
      if (!sources) {
        toast.error("Daftar resep sumber belum dapat dimuat.");
        setRecipeSources([]);
        return;
      }

      setRecipeSources(sources);
    } finally {
      setIsCopySourcesLoading(false);
    }
  };

  const handleApplyCopiedRecipe = (source, mode) => {
    const copiedMaterials = buildRecipeDraft(source?.materials || [], materials, optionUnit);
    if (!copiedMaterials.length) {
      toast.error("Resep sumber tidak memiliki bahan yang dapat disalin.");
      return;
    }

    setSelectedMaterials((currentMaterials) =>
      mode === "merge" ? mergeRecipeDraft(currentMaterials, copiedMaterials, optionUnit) : copiedMaterials,
    );
    setSearchTerm("");
    setIsCopyRecipeOpen(false);

    const sourceName = [source?.group_name || source?.product_name, source?.variant_label].filter(Boolean).join(" · ");
    toast.success(`Resep ${sourceName || "terpilih"} sudah disalin sebagai draft.`);
  };

  const handleSave = async () => {
    setValidationAttempted(true);
    if (selectedMaterials.length === 0) {
      toast.error("Pilih minimal satu bahan baku.");
      return;
    }
    if (hasInvalidQuantity) {
      toast.error("Isi/quantity setiap bahan baku wajib lebih dari 0.");
      return;
    }

    const recipeData = prepareRecipeData();
    setIsSaving(true);
    let success = false;
    try {
      success = isEdit ? await updateProductRecipe(recipeData?.id, recipeData) : await addProductRecipe(recipeData);
    } finally {
      setIsSaving(false);
    }

    if (success) {
      document.querySelector("[data-recipe-close]")?.click();
    }
  };

  return (
    <div
      className="modal modal-blur fade"
      id="modal-form-recipe"
      tabIndex="-1"
      role="dialog"
      aria-hidden="false"
      data-bs-backdrop="static"
      data-bs-keyboard="false"
      style={{
        backgroundColor: "rgba(0, 0, 0, 0.3)",
        backdropFilter: "blur(4px)",
      }}>
      <div className="modal-dialog modal-dialog-centered modal-xl akas-recipe-modal-dialog" role="document">
        <div className="modal-content akas-recipe-modal-content">
          <div className="modal-header akas-recipe-modal-header">
            <div className="akas-recipe-title-wrap">
              <span className="akas-recipe-title-icon" aria-hidden="true">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round">
                  <path d="M4 7h16M4 12h16M4 17h16" />
                  <rect x="8" y="3" width="8" height="4" rx="1" />
                </svg>
              </span>
              <div>
                <span className="akas-recipe-eyebrow">RESEP PRODUK</span>
                <h5 className="modal-title">{formState?.name || "Produk"}</h5>
                <p>
                  {[formState?.variant_label, `Resep untuk 1 ${formState?.unit || "porsi"}`]
                    .filter(Boolean)
                    .join(" · ")}
                </p>
              </div>
            </div>
            <div className="akas-recipe-header-actions">
              <button
                type="button"
                className="btn btn-outline-primary btn-sm"
                aria-label="Salin resep dari produk lain"
                disabled={isCopySourcesLoading}
                onClick={handleOpenCopyRecipe}>
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                  <rect x="8" y="8" width="11" height="11" rx="2" />
                  <path d="M16 8V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h2" />
                </svg>
                <span>{isCopySourcesLoading ? "Memuat..." : "Salin resep"}</span>
              </button>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Tutup"
                data-recipe-close
                onClick={closeModal}></button>
            </div>
          </div>
          <div className="modal-body akas-recipe-modal-body">
            <FormValidationNotice
              errors={validationAttempted && (selectedMaterials.length === 0 || hasInvalidQuantity) ? { materials: true } : {}}
              title={selectedMaterials.length === 0 ? "Pilih minimal satu bahan baku untuk resep." : "Periksa jumlah bahan baku yang ditandai."}
            />
            <section className="akas-recipe-summary" aria-label="Ringkasan resep">
              <article>
                <span>Harga jual</span>
                <strong className="text-primary">{formatIDR(productPrice)}</strong>
                <small>Per {formState?.unit || "porsi"}</small>
              </article>
              <article>
                <span>Estimasi HPP</span>
                <strong>{formatIDR(recipeCost)}</strong>
                <small>Dari harga material terbaru</small>
              </article>
              <article>
                <span>Margin kotor</span>
                <strong>{canAssessMargin ? formatIDR(grossMargin) : "—"}</strong>
                <small className={`akas-recipe-margin-status ${marginStatus.className}`}>
                  {canAssessMargin ? `${formatPercent(grossMarginPercent)} · ` : ""}{marginStatus.label}
                </small>
              </article>
              <article>
                <span>Komposisi</span>
                <strong>{selectedMaterials.length} material</strong>
                <small>{risingMaterials.length} harga naik · {fallingMaterials.length} turun</small>
              </article>
            </section>
            {hasUnsafeMargin ? (
              <div className="alert alert-danger mb-3" role="alert">
                <strong>HPP tidak sehat.</strong> Estimasi HPP {formatIDR(calculateTotalCost())} sudah sama atau lebih
                tinggi dari harga jual {formatIDR(formState?.unit_price || 0)}. Periksa kembali jumlah bahan atau harga
                produk sebelum resep digunakan.
              </div>
            ) : null}
            <div className="row">
              <div className="col-12">
                {/* Search Section - Tabler Style */}
                {/* <div className="card card-sm mb-3" bg>
                  <div className="card-body p-0 "></div>
                </div> */}
                <div className="akas-recipe-mobile-tabs" role="tablist" aria-label="Bagian resep">
                  <button
                    type="button"
                    role="tab"
                    aria-selected={activeMobilePanel === "recipe"}
                    className={activeMobilePanel === "recipe" ? "is-active" : ""}
                    onClick={() => setActiveMobilePanel("recipe")}>
                    Komposisi <span>{selectedMaterials.length}</span>
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={activeMobilePanel === "catalog"}
                    className={activeMobilePanel === "catalog" ? "is-active" : ""}
                    onClick={() => setActiveMobilePanel("catalog")}>
                    Tambah material <span>{filteredMaterials.length}</span>
                  </button>
                </div>

                <div className="row g-3 akas-recipe-workspace">
                  {/* reverse position on 12 col */}
                  <div className={`col-xl-5 col-12 order-xl-1 order-2 akas-recipe-panel akas-recipe-catalog-panel ${activeMobilePanel === "catalog" ? "is-mobile-active" : ""}`}>
                    {/* Daftar Bahan Baku - Tabler Table Style */}
                    <div className="card akas-recipe-workspace-card">
                      <div className="card-header akas-recipe-panel-header">
                        <div>
                          <span className="akas-recipe-panel-eyebrow">KATALOG</span>
                          <h3 className="card-title">Material tersedia</h3>
                        </div>
                        <div className="card-actions">
                          <span className="badge bg-secondary-lt">{filteredMaterials.length}/{materials?.length || 0}</span>
                        </div>
                      </div>
                      <div className="akas-recipe-catalog-filters">
                        <div className="input-icon">
                          <span className="input-icon-addon">
                            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                              <circle cx="10" cy="10" r="7" />
                              <path d="m21 21l-6-6" />
                            </svg>
                          </span>
                          <input
                            type="search"
                            className="form-control"
                            placeholder="Cari nama atau kode material..."
                            value={searchTerm}
                            onChange={(event) => setSearchTerm(event.target.value)}
                          />
                        </div>
                        <div className="akas-recipe-catalog-filter-row">
                          <AkasSelect
                            value={categoryFilter}
                            options={categoryOptions}
                            onChange={(event) => setCategoryFilter(event.target.value)}
                            aria-label="Filter kategori material"
                          />
                          <div className="akas-recipe-stock-filter" role="group" aria-label="Filter stok material">
                            {[{ value: "all", label: "Semua" }, { value: "available", label: "Tersedia" }].map((option) => (
                              <button
                                type="button"
                                key={option.value}
                                className={stockFilter === option.value ? "is-active" : ""}
                                aria-pressed={stockFilter === option.value}
                                onClick={() => setStockFilter(option.value)}>
                                {option.label}
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>
                      <div className="card-body p-0 akas-recipe-catalog-scroll">
                        <div className="table-responsive">
                          <table className="table table-vcenter card-table akas-recipe-catalog-table">
                            <thead>
                              <tr>
                                <th>Bahan Baku</th>
                                <th className="text-start">Stock</th>
                                <th className="text-center">Harga</th>
                                <th className="w-1" aria-label="Aksi"></th>
                              </tr>
                            </thead>
                            <tbody>
                              {filteredMaterials && filteredMaterials.length > 0 ? (
                                filteredMaterials?.map((material) => {
                                  const isChecked = selectedMaterials.some((m) => m.id === material.id);
                                  const baseUnitLabel = getBaseUnitLabel(optionUnit, material?.unit);
                                  const priceUnitLabel = getPriceUnitLabel(optionUnit, material?.unit);

                                  return (
                                    <tr
                                      key={material.id}
                                      className={isChecked ? "table-active" : ""}
                                      style={{
                                        cursor: "pointer",
                                      }}
                                      onClick={() => handleMaterialToggle(material)}>
                                      <td data-label="Nama">
                                        <div className="d-flex py-1 align-items-center">
                                          <UserAvatar
                                            fullName={material?.name}
                                            imageUrl={material?.image_path || material?.image}
                                            image_path={material?.image_path || material?.image}
                                            size="md"
                                            className="me-2"
                                            justPhotoView={true}
                                          />

                                          <div className="flex-fill">
                                            <div className="font-weight-medium">{material?.name}</div>
                                            <div className="d-flex flex-wrap gap-1 text-secondary text-capitalize">
                                              <span className="text-reset">
                                                {material?.type_label || material?.type || "-"}
                                              </span>
                                              {material?.category_label || material?.category ? (
                                                <span className="badge bg-secondary-lt">
                                                  {material?.category_label || material?.category}
                                                </span>
                                              ) : null}
                                            </div>
                                          </div>
                                        </div>
                                      </td>
                                      <td
                                        className={`text-start fw-bold ${
                                          material.stock <= material.min_stock ? "text-danger" : "text-success"
                                        }`}>
                                        {formatQuantity(material.stock)} {baseUnitLabel}
                                      </td>
                                      <td className="text-center fw-bold">
                                        {(() => {
                                          const trendMeta = getPriceTrendMeta(material?.price_trend);

                                          return (
                                            <div className="d-flex flex-column align-items-center gap-1">
                                              <span>
                                                {formatIDR(getLatestUnitCost(material))} / {priceUnitLabel}
                                              </span>
                                              <span className={`badge ${trendMeta.className}`}>
                                                <span className="me-1">{trendMeta.icon}</span>
                                                {trendMeta.label}
                                              </span>
                                            </div>
                                          );
                                        })()}
                                      </td>
                                      <td className="text-end">
                                        <button
                                          type="button"
                                          className={`btn btn-sm akas-recipe-add-button ${isChecked ? "is-selected" : "btn-outline-primary"}`}
                                          aria-label={isChecked ? `Batalkan pilihan ${material.name}` : `Tambahkan ${material.name} ke resep`}
                                          onClick={(event) => {
                                            event.stopPropagation();
                                            handleMaterialToggle(material);
                                          }}>
                                          {isChecked ? "✓" : "+"}
                                        </button>
                                      </td>
                                    </tr>
                                  );
                                })
                              ) : (
                                <tr>
                                  <td colSpan="6" className="text-center py-5">
                                    <div className="empty">
                                      <div className="empty-img">
                                        <svg
                                          xmlns="http://www.w3.org/2000/svg"
                                          width="48"
                                          height="48"
                                          viewBox="0 0 24 24"
                                          fill="none"
                                          stroke="currentColor"
                                          strokeWidth="1"
                                          strokeLinecap="round"
                                          strokeLinejoin="round">
                                          <circle cx="12" cy="12" r="10"></circle>
                                          <line x1="12" y1="8" x2="12" y2="12"></line>
                                          <line x1="12" y1="16" x2="12.01" y2="16"></line>
                                        </svg>
                                      </div>
                                      {hasActiveCatalogFilter ? (
                                        <>
                                          <p className="empty-title">Tidak ditemukan</p>
                                          <p className="empty-subtitle text-muted">
                                            Tidak ada material yang sesuai dengan pencarian atau filter.
                                          </p>
                                          <button
                                            type="button"
                                            className="btn btn-link"
                                            onClick={() => {
                                              setSearchTerm("");
                                              setCategoryFilter("all");
                                              setStockFilter("all");
                                            }}>
                                            Reset filter
                                          </button>
                                        </>
                                      ) : (
                                        <p className="empty-title">Tidak ada data</p>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                      <div className="akas-recipe-catalog-status">
                        <span><strong>{selectedMaterials.length}</strong> material dalam resep</span>
                        <button type="button" onClick={() => setActiveMobilePanel("recipe")}>Lihat komposisi</button>
                      </div>
                    </div>
                  </div>
                  <div className={`col-xl-7 col-12 order-xl-2 order-1 akas-recipe-panel akas-recipe-selected-column ${activeMobilePanel === "recipe" ? "is-mobile-active" : ""}`}>
                    {/* Section Bahan Baku yang Dipilih - Tabler Cards Style */}
                    <div
                      className="card akas-recipe-selected-card akas-recipe-workspace-card"
                      style={{
                        display: "block",
                      }}>
                      <div className="card-header akas-recipe-panel-header">
                        <div>
                          <span className="akas-recipe-panel-eyebrow">RECIPE BUILDER</span>
                          <h3 className="card-title">Komposisi resep</h3>
                        </div>
                        <div className="card-actions">
                          <span className="badge bg-primary-lt text-primary me-2">{selectedMaterials.length} material</span>
                          <button
                            type="button"
                            className="btn btn-outline-danger btn-sm"
                            disabled={selectedMaterials.length === 0}
                            data-bs-toggle="modal"
                            data-bs-target="#modal-clear-product-recipe">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              className="me-1">
                              <path d="M3 6h18" />
                              <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
                              <path d="M8 4V3c0-1 1-2 2-2h4c1 0 2 1 2 2v1" />
                              <line x1="10" y1="11" x2="10" y2="17" />
                              <line x1="14" y1="11" x2="14" y2="17" />
                            </svg>
                            Kosongkan
                          </button>
                        </div>
                      </div>

                      <div className="card-body m-0 p-0">
                        {selectedMaterials.length > 0 ? (
                          <>
                            <div className="table-responsive akas-input-table-scroll">
                              <table className="table table-vcenter card-table akas-input-table akas-recipe-input-table">
                                <thead>
                                  <tr>
                                    <th className="akas-recipe-col-number">no</th>
                                    <th className="akas-recipe-col-material">Bahan Baku</th>
                                    <th className="text-center akas-recipe-col-quantity">Berat/Qty</th>
                                    {/* <th
                                    className="text-start"
                                    style={{
                                      width: "25%",
                                    }}>
                                    Qty
                                    </th> */}
                                    <th className="text-center akas-recipe-col-subtotal">Subtotal</th>
                                    <th className="text-center px-0 py-0 akas-recipe-col-action"></th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {selectedMaterials.length > 0 ? (
                                    selectedMaterials?.map((material, index) => {
                                      const quantityValue = isWeightOrVolumeUnit(material?.unit, optionUnit)
                                        ? material?.weight
                                        : material?.qty;
                                      const baseQuantity = calculateBaseQuantity(material, optionUnit);
                                      const baseUnitLabel = getBaseUnitLabel(optionUnit, material?.unit);
                                      const priceUnitLabel = getPriceUnitLabel(optionUnit, material?.unit);
                                      const showBaseConversion =
                                        isWeightOrVolumeUnit(material?.unit, optionUnit) &&
                                        String(material?.unit).toLowerCase() !==
                                          String(getBaseUnitValue(optionUnit, material?.unit)).toLowerCase();
                                      const isQuantityInvalid = !isPositiveRecipeDecimal(quantityValue);
                                      const showQuantityError = validationAttempted && isQuantityInvalid;

                                      return (
                                        <tr key={material.id}>
                                          <td className="akas-recipe-col-number text-center">{index + 1}.</td>
                                          <td data-label="Nama" className="akas-recipe-col-material">
                                            <div className="d-flex py-1 align-items-center gap-2">
                                              <UserAvatar
                                                fullName={material?.name}
                                                imageUrl={material?.image_path || material?.image}
                                                image_path={material?.image_path || material?.image}
                                                size="sm"
                                                justPhotoView={true}
                                              />
                                              <div className="flex-fill">
                                                <div className="font-weight-medium fw-bold">{material?.name}</div>
                                                <div className="d-flex flex-wrap align-items-center gap-1 text-secondary text-capitalize akas-recipe-material-meta">
                                                  {material?.category_label || material?.category ? (
                                                    <span className="badge bg-secondary-lt">
                                                      {material?.category_label || material?.category}
                                                    </span>
                                                  ) : null}
                                                  <span className="small text-lowercase">
                                                    Harga {formatIDR(material?.unit_cost || 0)} / {priceUnitLabel}
                                                  </span>
                                                  {(() => {
                                                    const trendMeta = getPriceTrendMeta(material?.price_trend);

                                                    return (
                                                      <span className={`badge ${trendMeta.className}`}>
                                                        <span className="me-1">{trendMeta.icon}</span>
                                                        {trendMeta.label}
                                                      </span>
                                                    );
                                                  })()}
                                                </div>
                                              </div>
                                            </div>
                                          </td>
                                          <td className="text-start fw-bold fs-5 akas-recipe-col-quantity">
                                            <div
                                              className="input-group flex-nowrap akas-dynamic-number-group"
                                              style={{ width: getQuantityInputWidth(quantityValue) }}>
                                              <NumberInput
                                                type="number"
                                                inputMode="decimal"
                                                className={`form-control fw-bold ps-2 pe-1 ${showQuantityError ? "is-invalid" : ""}`}
                                                value={quantityValue ?? ""}
                                                aria-invalid={showQuantityError}
                                                placeholder="Isi"
                                                onChange={(e) => {
                                                  const inputValue = sanitizeRecipeDecimalInput(e.target.value);

                                                  const updatedMaterials = selectedMaterials.map((m) =>
                                                    m.id === material.id
                                                      ? {
                                                          ...m,
                                                          weight: isWeightOrVolumeUnit(m?.unit, optionUnit)
                                                            ? inputValue
                                                            : m?.weight,
                                                          qty: isWeightOrVolumeUnit(m?.unit, optionUnit)
                                                            ? m?.qty
                                                            : inputValue,
                                                          sub_total: calculateIngredientCost(
                                                            {
                                                              ...m,
                                                              weight: isWeightOrVolumeUnit(m?.unit, optionUnit)
                                                                ? inputValue
                                                                : m?.weight,
                                                              qty: isWeightOrVolumeUnit(m?.unit, optionUnit)
                                                                ? m?.qty
                                                                : inputValue,
                                                            },
                                                            optionUnit,
                                                          ),
                                                        }
                                                      : m,
                                                  );

                                                  setSelectedMaterials(updatedMaterials);
                                                }}
                                                onBlur={(event) => {
                                                  const normalizedValue = formatRecipeDecimal(event.target.value);
                                                  setSelectedMaterials((currentMaterials) =>
                                                    currentMaterials.map((currentMaterial) =>
                                                      currentMaterial.id === material.id
                                                        ? {
                                                            ...currentMaterial,
                                                            weight: isWeightOrVolumeUnit(
                                                              currentMaterial?.unit,
                                                              optionUnit,
                                                            )
                                                              ? normalizedValue
                                                              : currentMaterial?.weight,
                                                            qty: isWeightOrVolumeUnit(currentMaterial?.unit, optionUnit)
                                                              ? currentMaterial?.qty
                                                              : normalizedValue,
                                                          }
                                                        : currentMaterial,
                                                    ),
                                                  );
                                                }}
                                              />

                                              <span className="input-group-text mx-0 p-1 fs-6">{material?.unit}</span>
                                            </div>
                                            {showBaseConversion ? (
                                              <div className="form-hint text-nowrap">
                                                Setara {formatQuantity(baseQuantity)} {baseUnitLabel}
                                              </div>
                                            ) : null}
                                            <div className={`form-hint text-nowrap ${Number(material?.stock || 0) <= 0 ? "text-danger" : ""}`}>
                                              Stok tersedia {formatQuantity(material?.stock)} {baseUnitLabel}
                                            </div>
                                            {showQuantityError ? (
                                              <div className="invalid-feedback d-block">Wajib lebih dari 0.</div>
                                            ) : null}
                                          </td>
                                          {/* <td className="text-center fw-bold">
                                        <div className="input-group">
                                        <input
                                          type="number"
                                          min="0"
                                          className="form-control fw-bold"
                                          value={material?.qty || 0}
                                          onWheel={(e) => e.target.blur()}
                                          onChange={(e) => {
                                            const updatedMaterials = selectedMaterials.map((m) =>
                                              m.id === material.id
                                                ? {
                                                    ...m,
                                                    qty:
                                                      m?.unit == "gram" || m?.unit == "ml"
                                                        ? m?.qty
                                                        : Number(e.target.value),
                                                    sub_total:
                                                      m?.unit == "gram" || m?.unit == "ml"
                                                        ? m?.unit_cost * e.target.value
                                                        : m?.unit_cost * e.target.value,
                                                  }
                                                : m,
                                            );
                                            setSelectedMaterials(updatedMaterials);
                                          }}
                                          disabled={
                                            material?.unit == "gram" || material?.unit == "ml" ? true : false
                                          }
                                        />
                                        <span className="input-group-text mx-0">pcs</span>
                                        </div>
                                        </td> */}
                                          <td className="text-center fw-bold akas-recipe-col-subtotal">
                                            {formatIDR(material?.sub_total || 0)}
                                            <div className="form-hint">HPP bahan</div>
                                          </td>
                                          <td className="text-center akas-recipe-col-action">
                                            <button
                                              type="button"
                                              className="btn btn-sm btn-icon btn-outline-danger"
                                              aria-label={`Hapus ${material?.name} dari resep`}
                                              onClick={() => handleMaterialToggle(material)}>
                                              <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                width="16"
                                                height="16"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth="2"
                                                strokeLinecap="round"
                                                strokeLinejoin="round">
                                                <line x1="18" y1="6" x2="6" y2="18"></line>
                                                <line x1="6" y1="6" x2="18" y2="18"></line>
                                              </svg>
                                            </button>
                                          </td>
                                        </tr>
                                      );
                                    })
                                  ) : (
                                    <tr>
                                      <td colSpan="6" className="text-center py-5">
                                        <div className="empty">
                                          <div className="empty-img">
                                            <svg
                                              xmlns="http://www.w3.org/2000/svg"
                                              width="48"
                                              height="48"
                                              viewBox="0 0 24 24"
                                              fill="none"
                                              stroke="currentColor"
                                              strokeWidth="1"
                                              strokeLinecap="round"
                                              strokeLinejoin="round">
                                              <circle cx="12" cy="12" r="10"></circle>
                                              <line x1="12" y1="8" x2="12" y2="12"></line>
                                              <line x1="12" y1="16" x2="12.01" y2="16"></line>
                                            </svg>
                                          </div>
                                          {searchTerm ? (
                                            <>
                                              <p className="empty-title">Tidak ditemukan</p>
                                              <p className="empty-subtitle text-muted">
                                                Bahan baku "{searchTerm}" tidak tersedia
                                              </p>
                                              <button className="btn btn-link" onClick={() => setSearchTerm("")}>
                                                Hapus filter
                                              </button>
                                            </>
                                          ) : (
                                            <p className="empty-title">Tidak ada data</p>
                                          )}
                                        </div>
                                      </td>
                                    </tr>
                                  )}
                                </tbody>
                                {/* <tfoot className="border-0">
                                </tfoot> */}
                              </table>
                            </div>
                            <div className="akas-recipe-cost-summary">
                              <div>
                                <span>Estimasi HPP resep</span>
                                <strong>{formatIDR(recipeCost)}</strong>
                              </div>
                              <div>
                                <span>Margin kotor</span>
                                <strong className={hasUnsafeMargin ? "text-danger" : "text-primary"}>
                                  {canAssessMargin ? formatIDR(grossMargin) : "—"}
                                </strong>
                              </div>
                              <span className={`akas-recipe-margin-status ${marginStatus.className}`}>
                                {canAssessMargin ? `${formatPercent(grossMarginPercent)} · ` : ""}{marginStatus.label}
                              </span>
                            </div>
                          </>
                        ) : (
                          <div className="empty">
                            <div className="empty-img">
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="64"
                                height="64"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="1"
                                strokeLinecap="round"
                                strokeLinejoin="round">
                                <circle cx="12" cy="12" r="10"></circle>
                                <line x1="12" y1="8" x2="12" y2="12"></line>
                                <line x1="12" y1="16" x2="12.01" y2="16"></line>
                              </svg>
                            </div>
                            <p className="empty-title">Belum ada bahan baku</p>
                            <p className="empty-subtitle text-muted">Silakan pilih bahan baku dari daftar</p>
                            <button
                              type="button"
                              className="btn btn-outline-primary btn-sm"
                              onClick={() => setActiveMobilePanel("catalog")}>
                              Tambah material
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-footer akas-recipe-modal-footer">
            <div className="akas-recipe-save-note">
              <span aria-hidden="true"></span>
              HPP dihitung otomatis dari harga material terbaru.
            </div>
            <div className="akas-recipe-footer-actions">
              <button type="button" className="btn btn-link link-secondary" onClick={closeModal} data-bs-dismiss="modal">
                Batal
              </button>
              {isEdit ? (
              <button
                className="btn btn-primary"
                onClick={handleSave}
                disabled={isSaving}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="me-1">
                  <path d="M7 7h-1a2 2 0 0 0 -2 2v9a2 2 0 0 0 2 2h9a2 2 0 0 0 2 -2v-1" />
                  <path d="M20.385 6.585a2.1 2.1 0 0 0 -2.97 -2.97l-8.415 8.385v3h3l8.385 -8.415z" />
                  <path d="M16 5l3 3" />
                </svg>
                {isSaving ? "Menyimpan..." : "Update Resep"}
              </button>
              ) : (
              <button
                className="btn btn-primary"
                onClick={handleSave}
                disabled={isSaving}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="me-1">
                  <path d="M12 5l0 14" />
                  <path d="M5 12l14 0" />
                </svg>
                {isSaving ? "Menyimpan..." : "Tambah Resep"}
              </button>
              )}
            </div>
          </div>
        </div>
      </div>
      <CopyRecipeModal
        isOpen={isCopyRecipeOpen}
        isLoading={isCopySourcesLoading}
        sources={recipeSources}
        targetProductId={formState?.id}
        targetName={formState?.name}
        existingMaterialCount={selectedMaterials.length}
        materials={materials}
        units={optionUnit}
        onClose={() => setIsCopyRecipeOpen(false)}
        onApply={handleApplyCopiedRecipe}
      />
      <AlertConfirmModal
        idModal="modal-clear-product-recipe"
        type="warning"
        title="Kosongkan komposisi resep?"
        message="Semua material yang sudah dipilih dan jumlah yang telah diisi akan dihapus dari draft resep ini."
        confirmText="Kosongkan resep"
        cancelText="Batal"
        waitForConfirm
        onConfirm={() => {
          setSelectedMaterials([]);
          return true;
        }}
      />
    </div>
  );
};

export default FormProductRecipe;
